import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { useRef } from "react";
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import { message } from 'antd';
import { RcFile } from "antd/lib/upload";
import { useLocation } from "react-router-dom";
import UserLoginContext from "../../context/userLoginContext";
import { IEmployeeDetail } from "../../domains/models/employee";
import { IFileState } from "./types/employee-details.types";
import EmployeeDetailView from "./employee-detail.view";
import DataLoading from "../../common/loading-data";
import WebcamCapture from "../../page-components/employee-detail/component/webcam-capture";
import server from "../../server";
import DocumentUploadTrigger from "../common/document-uploader/utils/document-upload-trigger.view";
import '../../styles/component/employee-detail.scss';


const getInitials = (e: any | null): string => {
    if (!e?.name) return '';
    const parts = [
        e.name.firstName?.trim(),
        e.name.middleName?.trim(),
        e.name.lastName?.trim()
    ].filter(Boolean);

    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const EmployeeDetail = () => {
    const prevUrl = useRef<string | null>(null);
    const {user, setUser} = useContext<any>(UserLoginContext);
    const [employeeData, setEmployeeData] = useState<IEmployeeDetail | null>(null);

    const location = useLocation();
    const isActiveEmployee = location.state?.isActive ?? true;


    const empId = location.state?.empId || user?.employeeId;

    const [viewState, setViewState] = useState({
        isModalVisible: false,
        popoverVisible: false,
    });

    const [profileState, setProfileState] = useState<IFileState>({
        uploadedFileName: '',
        documentId: '',
        profileUrl: {
            baseLocation: '',
            id: ''
        }
    });


    const hasProfilePhoto = Boolean(profileState.profileUrl?.baseLocation);

    const navigate = useNavigate();

    function handleNoDataFound() {
        navigate('/error');
    }

    const navigateToDocument = () => {
        navigate('/employee/documents', {
            state: employeeData?.id,
        });
    };

    const navigateToEmployeeEdit = () => {
        navigate(`/employee/create/personal`, {state: {empId: employeeData?.id}})
    };

    useEffect(() => {
        if (!empId) return;
        fetchDetails();
    }, [empId]);


    useEffect(() => {
        if (!empId && !profileState.documentId) return;
        let canceled = false;
        let currentUrl: string | null = null;

        const fetchAndSetUserProfile = async () => {
            try {
                let id = profileState.documentId;

                // Fetch documentId only if not already present
                if (!id) {
                    const params = {employeeId: empId, documentType: 'PROFILE_PHOTO', purpose: 'PROFILE_PHOTO'}
                    const resp = await server.document.find(params);

                    if (!resp || resp.length === 0) return;

                    id = resp[0].id;
                }

                const blob = await server.document.download(id);
                if (!(blob instanceof Blob)) return;

                currentUrl = URL.createObjectURL(blob);

                // cleanup previous blob
                if (prevUrl.current) {
                    URL.revokeObjectURL(prevUrl.current);
                }

                prevUrl.current = currentUrl;
                console.log(currentUrl, 'url recieved')
                setProfileState(prev => ({
                    ...prev,
                    documentId: id,
                    profileUrl: {
                        baseLocation: currentUrl,
                        id: id
                    }
                }));

            } catch (err) {
                console.error('Failed to load image', err);
            }
        };

        fetchAndSetUserProfile();

        return () => {
            canceled = true;
            if (currentUrl) URL.revokeObjectURL(currentUrl);
            if (prevUrl.current === currentUrl) {
                prevUrl.current = null;
            }
        };
    }, [
        empId,
        profileState.documentId,
        profileState.uploadedFileName
    ]);

    async function fetchDetails() {
        try {
            const response = await server.employee.employeeDetailsByID(empId, {
                "include-family-details": true,
                "include-experiences": true,
                "include-bank-detail": true
            });
            setEmployeeData(response);
        } catch (e) {
            console.log('API Encountered Error', e);
            handleNoDataFound();
        }
    }

    const uploadFile = useCallback(async (opts: UploadRequestOption<any>) => {
        const {file, onSuccess, onError} = opts;
        try {
            if (!profileState.documentId) {
                const uploadResponse: any = await server.document.upload(file, "PROFILE_PHOTO", undefined, empId);
                onSuccess?.(uploadResponse, file as RcFile);
                setProfileState((prev) => ({
                    ...prev,
                    uploadedFileName: uploadResponse?.fileName
                }))
            }
        } catch (err) {
            onError?.(err as Error);
            message.error("Upload failed");
        }
    }, [empId, profileState.documentId]);

    const handleRemove = useCallback(async () => {
        if (!empId || !profileState.profileUrl?.id) return;

        try {
            if (profileState.documentId)
                await server.document.delete(profileState.documentId);

            setProfileState((prev) => ({
                ...prev,
                documentId: '',
                uploadedFileName: '',
                profileUrl: {
                    baseLocation: '',
                    id: ''
                }
            }))
            setUser((prev: any) => ({
                ...prev,
                profileURL: '',
            }));
        } catch (err) {
            message.error('Failed to delete photo');
        }
    }, [empId, profileState.profileUrl]);

    async function handleDocumentUpload(e: any) {
        const res = await server.document.upload(e.file, 'PROFILE_PHOTO', undefined, empId);
        setProfileState((prev) => ({
            ...prev,
            uploadedFileName: res?.fileName,
        }))
    }

    const reloadPreview = async () => {
        if (!empId) return;
        const params = {employeeId: empId, documentType: "PROFILE_PHOTO"}
        const resp = await server.document.find(params);
        if (!resp?.length) return;

        const info = resp[0];

        const blob = await server.document.download(info.id);
        const url = URL.createObjectURL(blob);
        URL.revokeObjectURL(prevUrl.current!);
        prevUrl.current = url;
        setUser((prev: any) => ({
            ...prev,
            profileURL: '',
        }));
        setProfileState((prev) => ({
            ...prev,
            documentId: info.id,
            uploadedFileName: info.fileName,
            profileUrl: {baseLocation: url, id: info.id}
        }))
    };

    const PopoverMenuItems = hasProfilePhoto ?
        [
            {
                key: 'view',
                label: 'View photo',
                onClick: () =>
                    setViewState((prev) => ({
                        ...prev,
                        popoverVisible: false,
                        isModalVisible: true,
                    })),
            },
            {
                key: 'remove',
                label: 'Remove photo',
                onClick: handleRemove,
            },
        ]
        : [
            {
                key: 'upload',
                label: (
                    <DocumentUploadTrigger
                        disabled={false}
                        handleDocumentUpload={handleDocumentUpload}
                        docType={"PROFILE_PHOTO"}
                    />
                ),
            },
            {
                key: 'camera',
                label: (
                    <WebcamCapture
                        uploadFile={uploadFile}
                        reloadPreview={reloadPreview}
                        hasProfilePhoto={hasProfilePhoto}
                    />
                ),
            },
        ];

    return (
        !employeeData ?
            (
                <DataLoading title="Employee Details"/>
            ) :
            (
                <EmployeeDetailView
                    viewState={viewState}
                    setViewState={setViewState}
                    navigateToDocument={navigateToDocument}
                    isActiveEmployee={isActiveEmployee}
                    getInitials={getInitials}
                    PopoverMenuItems={PopoverMenuItems}
                    profileState={profileState}
                    employeeData={employeeData}
                    navigateToEmployeeEdit={navigateToEmployeeEdit}
                    user={user}
                />
            )
    )
}

export default EmployeeDetail;
